/**
 * Specification List Component
 *
 * Displays specifications in a card grid with filtering and pagination.
 * Part of PowerSpec V3 - Spec-Driven Development
 *
 * Features:
 * - Filter by status (draft, in_review, approved, published)
 * - Filter by template type
 * - Search by title/content
 * - Pagination
 * - Create new spec button
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Button,
  Spinner,
  Alert,
  Select,
  Label,
  TextInput
} from 'flowbite-react';
import {
  HiPlus,
  HiSearch,
  HiDocumentText,
  HiChevronLeft,
  HiChevronRight
} from 'react-icons/hi';
import SpecCard from './SpecCard';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

export interface Specification {
  id: string;
  business_id: string;
  title: string;
  status: 'draft' | 'in_review' | 'approved' | 'published';
  tags: string[];
  version: number;
  view_count: number;
  connected_repo: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  template_name: string;
  template_category: string;
}

export interface SpecListMetadata {
  Total_Records: number;
  Page: number;
  Limit: number;
  Total_Pages: number;
}

interface SpecListProps {
  clientId: string;
  projectId?: string;
  onCreateSpec?: () => void;
  onViewSpec?: (spec: Specification) => void;
  onEditSpec?: (spec: Specification) => void;
  onDeleteSpec?: (spec: Specification) => void;
  onRefresh?: () => void;
}

export const SpecList: React.FC<SpecListProps> = ({
  clientId,
  projectId,
  onCreateSpec,
  onViewSpec,
  onEditSpec,
  onDeleteSpec,
  onRefresh
}) => {
  const [specs, setSpecs] = useState<Specification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(20);
  const [metadata, setMetadata] = useState<SpecListMetadata | null>(null);

  const fetchSpecs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params: Record<string, string> = {
        Client_ID: clientId,
        page: currentPage.toString(),
        limit: pageLimit.toString()
      };

      if (projectId) {
        params.Project_ID = projectId;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (templateFilter !== 'all') {
        params.template_id = templateFilter;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await Make_Authorized_API_Call<Specification>(
        API_ENDPOINTS.SPECIFICATIONS.LIST,
        'GET',
        undefined,
        { params }
      );

      if (isSuccessResponse(response)) {
        setSpecs(response.data?.Records || []);

        if (response.meta) {
          const meta = response.meta as { Total_Records: number; Page?: number; Limit?: number; Total_Pages?: number };
          setMetadata({
            Total_Records: meta.Total_Records,
            Page: meta.Page || 1,
            Limit: meta.Limit || 20,
            Total_Pages: meta.Total_Pages || 1
          });
        }
      } else {
        throw new Error(response.message || 'API returned unsuccessful response');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load specifications';
      console.error('[SpecList] Error fetching specs:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId, statusFilter, templateFilter, searchQuery, currentPage, pageLimit]);

  useEffect(() => {
    if (clientId) {
      fetchSpecs();
    }
  }, [clientId, fetchSpecs]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleTemplateFilterChange = (value: string) => {
    setTemplateFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleNextPage = () => {
    if (metadata && currentPage < metadata.Total_Pages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSpecDeleted = () => {
    fetchSpecs();
    onRefresh?.();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading specifications</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            size="xs"
            color="light"
            className="mt-2"
            onClick={fetchSpecs}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Specifications</h2>
        {onCreateSpec && (
          <Button onClick={onCreateSpec}>
            <HiPlus className="w-5 h-5 mr-2" />
            Create Specification
          </Button>
        )}
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <Label htmlFor="search" value="Search" className="mb-2 block" />
            <TextInput
              id="search"
              type="text"
              placeholder="Search by title or content..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              icon={HiSearch}
            />
          </div>

          {/* Status Filter */}
          <div>
            <Label htmlFor="status-filter" value="Status" className="mb-2 block" />
            <Select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
            </Select>
          </div>

          {/* Template Filter */}
          <div>
            <Label htmlFor="template-filter" value="Template Type" className="mb-2 block" />
            <Select
              id="template-filter"
              value={templateFilter}
              onChange={(e) => handleTemplateFilterChange(e.target.value)}
            >
              <option value="all">All Templates</option>
              {/* Template options would be populated from API */}
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600">
          {metadata ? (
            <>
              Showing {((currentPage - 1) * pageLimit) + 1} - {Math.min(currentPage * pageLimit, metadata.Total_Records)} of {metadata.Total_Records} specification{metadata.Total_Records !== 1 ? 's' : ''}
            </>
          ) : (
            <>
              {specs.length} specification{specs.length !== 1 ? 's' : ''}
            </>
          )}
        </div>
      </Card>

      {/* Specification Grid */}
      {specs.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <HiDocumentText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No specifications found</p>
            <p className="text-sm text-gray-400">
              {searchQuery || statusFilter !== 'all' || templateFilter !== 'all'
                ? 'Try adjusting your filters or search query'
                : 'Click "Create Specification" to add your first spec'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {specs.map((spec) => (
            <SpecCard
              key={spec.id}
              spec={spec}
              onView={() => onViewSpec?.(spec)}
              onEdit={() => onEditSpec?.(spec)}
              onDelete={() => {
                onDeleteSpec?.(spec);
                handleSpecDeleted();
              }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {metadata && metadata.Total_Pages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <Button
              size="sm"
              color="light"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              <HiChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </Button>

            <div className="text-sm text-gray-600">
              Page {currentPage} of {metadata.Total_Pages}
            </div>

            <Button
              size="sm"
              color="light"
              onClick={handleNextPage}
              disabled={currentPage >= metadata.Total_Pages}
            >
              Next
              <HiChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SpecList;
